import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Loader2, Building2, TrendingDown, Target, ShieldPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { loanProfilingApi } from '@/services/api';
import { SectionHeading, inr } from './CockpitUI';
import { Field, Picker } from './OfferPieces';
import { OfferRow, OfferPanel } from './OfferCard';

const AddOfferForm = ({ leadId, partnerBanks, defaultAmount, onCreated, onCancel }) => {
  const [f, setF] = useState({
    bank_name: '', bank_code: '', bank_poc: '', roi: '14', tenure_months: '48',
    commission_pct: '4.5', loan_amount: defaultAmount || '', emi_date: '', note: '',
  });
  const [saving, setSaving] = useState(false);

  const pickBank = (code) => {
    const bank = (partnerBanks || []).find((b) => b.bank_code === code || b.id === code);
    setF((p) => ({ ...p, bank_code: code, bank_name: bank ? bank.bank_name : p.bank_name }));
  };

  const create = async () => {
    if (!f.bank_name.trim()) { toast.message('Which bank is this offer from?'); return; }
    setSaving(true);
    try {
      const res = await loanProfilingApi.createOffer(leadId, {
        bank_name: f.bank_name.trim(),
        bank_code: f.bank_code || null,
        bank_poc: f.bank_poc || null,
        roi: f.roi === '' ? null : parseFloat(f.roi),
        tenure_months: f.tenure_months === '' ? null : parseInt(f.tenure_months, 10),
        commission_pct: f.commission_pct === '' ? null : parseFloat(f.commission_pct),
        loan_amount: f.loan_amount === '' ? null : parseFloat(f.loan_amount),
        emi_date: f.emi_date || null,
        note: f.note || null,
      });
      onCreated(res.data);
      toast.success(`${f.bank_name} offer added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add this offer');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 mb-4" data-testid="offer-add-form">
      <h4 className="text-sm font-semibold text-slate-800 mb-3">What has the bank quoted?</h4>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Partner bank">
          <Picker value={f.bank_code} onChange={pickBank} placeholder="Pick a bank"
            options={(partnerBanks || []).map((b) => ({ value: b.bank_code || b.id, label: b.bank_name }))}
            testId="offer-bank-picker" />
        </Field>
        <Field label="Bank name" hint="Or type any lender">
          <Input value={f.bank_name} onChange={(e) => setF((p) => ({ ...p, bank_name: e.target.value }))}
            className="mt-1 h-9" placeholder="Kotak Mahindra Prime" data-testid="offer-bank-name" />
        </Field>
        <Field label="Rate (%)">
          <Input type="number" step="0.05" value={f.roi} onChange={(e) => setF((p) => ({ ...p, roi: e.target.value }))}
            className="mt-1 h-9" data-testid="offer-roi-input" />
        </Field>
        <Field label="Tenure (months)">
          <Input type="number" value={f.tenure_months} onChange={(e) => setF((p) => ({ ...p, tenure_months: e.target.value }))}
            className="mt-1 h-9" data-testid="offer-tenure-input" />
        </Field>
        <Field label="Loan amount (₹)">
          <Input type="number" value={f.loan_amount} onChange={(e) => setF((p) => ({ ...p, loan_amount: e.target.value }))}
            className="mt-1 h-9" data-testid="offer-amount-input" />
        </Field>
        <Field label="Payout to us (%)" hint="Internal only">
          <Input type="number" step="0.05" value={f.commission_pct} onChange={(e) => setF((p) => ({ ...p, commission_pct: e.target.value }))}
            className="mt-1 h-9" data-testid="offer-commission-input" />
        </Field>
        <Field label="EMI date">
          <Input value={f.emi_date} onChange={(e) => setF((p) => ({ ...p, emi_date: e.target.value }))}
            className="mt-1 h-9" placeholder="5th of every month" data-testid="offer-emidate-input" />
        </Field>
        <Field label="Bank contact">
          <Input value={f.bank_poc} onChange={(e) => setF((p) => ({ ...p, bank_poc: e.target.value }))}
            className="mt-1 h-9" placeholder="Shashi, Kotak" data-testid="offer-poc-input" />
        </Field>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="offer-add-cancel">Cancel</Button>
        <Button size="sm" onClick={create} disabled={saving} data-testid="offer-add-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}Add offer
        </Button>
      </div>
    </div>
  );
};

const Overview = ({ offers, comparison, ask, benefits }) => {
  const live = offers.filter((o) => o.status !== 'NOT_SELECTED');
  const best = offers.find((o) => o.id === comparison?.lowest_emi_offer_id);
  const accepted = offers.find((o) => o.id === comparison?.accepted_offer_id);
  const asked = !!(ask?.roi || ask?.emi);
  if (live.length < 1) return null;

  return (
    <div className="space-y-2 mb-3" data-testid="offers-overview">
      {asked && (
        <div className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-2"
          data-testid="offers-customer-ask">
          <Target className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Customer wants{' '}
          {ask.roi ? <strong>{ask.roi}% or better</strong> : null}
          {ask.roi && ask.emi ? ' and ' : null}
          {ask.emi ? <strong>an EMI of {inr(ask.emi)} or less</strong> : null}
        </div>
      )}
      {accepted && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-2 font-medium"
          data-testid="offers-accepted">
          Customer chose {accepted.bank_name}
        </div>
      )}
      <div className="text-xs text-slate-500 px-0.5 space-y-1">
        <div data-testid="offers-in-play">
          <Building2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-slate-400" />
          <strong className="text-slate-800">{live.length}</strong> offer{live.length > 1 ? 's' : ''} in play
        </div>
        {best && (
          <div data-testid="offers-best">Lowest EMI: <strong className="text-slate-800">{best.bank_name}</strong> at {best.roi}% · {inr(best.emi)}</div>
        )}
        {comparison?.spread?.emi_gap > 0 && (
          <div data-testid="offers-spread">
            <TrendingDown className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-emerald-500" />
            {inr(comparison.spread.emi_gap)} a month between the best and the worst
          </div>
        )}
        {benefits?.count > 0 && (
          <div data-testid="offers-benefits-note">
            <ShieldPlus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-indigo-500" />
            Every letter carries {benefits.count} Wisedrive benefit{benefits.count > 1 ? 's' : ''}
            {benefits.total_worth > 0 ? <> worth {inr(benefits.total_worth)}</> : null}
          </div>
        )}
      </div>
      {comparison?.mixed_loan_amounts && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2"
          data-testid="offers-amount-warning">
          These offers are for different loan amounts — compare the EMIs with that in mind.
        </div>
      )}
    </div>
  );
};

export const OffersLetter = ({ leadId, partnerBanks, requiredLoanAmount, onChanged }) => {
  const [state, setState] = useState({ offers: [], comparison: {}, context: {} });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await loanProfilingApi.listOffers(leadId);
      setState(res.data);
    } catch (err) {
      toast.error('Could not load the offers');
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const apply = (data) => {
    setState(data);
    setAdding(false);
    if (onChanged) onChanged();
  };

  if (loading) {
    return (
      <div className="py-16 flex justify-center" data-testid="offers-loading">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const { offers, comparison } = state;
  // the bank the customer picked is what a rep opens the file for
  const selected = offers.find((o) => o.id === selectedId)
    || offers.find((o) => o.id === comparison?.accepted_offer_id)
    || offers[0];
  const fileDisbursed = offers.some((x) => x.status === 'DISBURSED');

  return (
    <div data-testid="offers-letter-tab">
      <div className="flex items-start justify-between gap-4 mb-4">
        <SectionHeading hint="Pick a bank on the left; its terms, negotiation and papers open on the right.">
          Offers Letter
        </SectionHeading>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)} data-testid="offer-add-btn">
            <Plus className="w-4 h-4 mr-1.5" />Add bank offer
          </Button>
        )}
      </div>

      {adding && (
        <AddOfferForm leadId={leadId} partnerBanks={partnerBanks}
          defaultAmount={requiredLoanAmount || state.context?.required_loan_amount}
          onCreated={(data) => {
            apply(data);
            // the server tells us which offer it just made — never assume the order
            const made = data.offer?.id || data.offers?.[data.offers.length - 1]?.id;
            setSelectedId(made || null);
          }}
          onCancel={() => setAdding(false)} />
      )}

      {offers.length === 0 && !adding ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="offers-empty">
          <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No bank offers on this file yet</p>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            Add what each bank has quoted — rate, tenure and the payout they give us — then negotiate and send the customer a letter per bank.
          </p>
          <Button size="sm" className="mt-4" onClick={() => setAdding(true)} data-testid="offers-empty-add-btn">
            <Plus className="w-4 h-4 mr-1.5" />Add the first offer
          </Button>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <aside className="w-full lg:w-[330px] xl:w-[360px] shrink-0 lg:sticky lg:top-2" data-testid="offers-bank-list">
            <Overview offers={offers} comparison={comparison} ask={state.context?.customer_ask}
              benefits={state.context?.service_benefits} />
            <div className="space-y-2">
              {offers.map((o) => (
                <OfferRow key={o.id} offer={o} comparison={comparison}
                  selected={selected?.id === o.id} onSelect={() => setSelectedId(o.id)} />
              ))}
            </div>
          </aside>
          <div className="flex-1 min-w-0 w-full">
            {selected && (
              <OfferPanel key={selected.id} leadId={leadId} offer={selected} comparison={comparison}
                onSaved={apply} fileDisbursed={fileDisbursed} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OffersLetter;
